/*
 * The application's answers, editable.
 *
 * An application is a snapshot of the day it was filled in, and these are the answers that go
 * stale fastest: an agency that grows from four clients to twenty, or moves off n8n, is exactly
 * the partner we most want to be right about. Until this existed the only way to fix one was a
 * database write, and the CRM would never have heard about it either.
 *
 * ONE component, used by the partner on their Profile and by an admin on the partner record,
 * because a partner and an admin looking at the same record must be looking at the same
 * questions. The only difference between the two is the endpoint it saves to, which is
 * `changed_by` on the event and nothing else.
 *
 * It POSTS ONLY WHAT CHANGED. The server ignores an empty edit, but a form that sends its whole
 * state on every save would have to be trusted to do that forever, and this way a save that
 * changes one field is one field on the wire.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { APPLICATION_QUESTIONS, APPLICATION_TEXT_FIELDS } from '../lib/application';
import { toast } from 'sonner';
import axios from 'axios';

const sameAnswer = (a, b) =>
  Array.isArray(a) || Array.isArray(b)
    ? [...(a || [])].sort().join('|') === [...(b || [])].sort().join('|')
    : (a || '') === (b || '');

const ApplicationAnswers = ({ endpoint, headers, answers, onSaved }) => {
  const [draft, setDraft] = useState(answers || {});
  const [saving, setSaving] = useState(false);

  // Re-seed when the record underneath changes — an admin switching partners in the same
  // dialog would otherwise be editing the last one's answers over this one's.
  useEffect(() => setDraft(answers || {}), [answers]);

  // What would go on the wire. Also what decides whether there is anything to save: an
  // untouched form must not offer a button that fires an event saying something changed.
  const patch = useMemo(() => {
    const out = {};
    Object.keys(draft || {}).forEach((field) => {
      if (!sameAnswer(draft[field], (answers || {})[field])) out[field] = draft[field];
    });
    return out;
  }, [draft, answers]);

  const dirty = Object.keys(patch).length > 0;

  const set = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const toggle = (field, value) =>
    setDraft((prev) => {
      const on = (prev[field] || []).includes(value);
      return {
        ...prev,
        [field]: on
          ? (prev[field] || []).filter((v) => v !== value)
          : [...(prev[field] || []), value],
      };
    });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(endpoint, patch, { headers });
      const count = Object.keys(data.changed || {}).length;
      toast.success(count ? `${count} answer${count > 1 ? 's' : ''} updated` : 'Nothing to update');
      if (onSaved) onSaved(data.answers);
    } catch (error) {
      // The server owns the vocabulary, so its message names the field and its options. Ours
      // would only be a vaguer version of the same sentence.
      toast.error(error.response?.data?.detail || 'Could not save those answers');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="application-answers">
      {APPLICATION_QUESTIONS.map((q) =>
        q.multi ? (
          <fieldset key={q.field}>
            <legend className="text-sm font-medium text-[#1A1A1A]">{q.label}</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2" data-testid={`answer-${q.field}`}>
              {q.options.map(([value, label]) => {
                const on = (draft[q.field] || []).includes(value);
                const id = `${q.field}-${value}`;
                return (
                  <div
                    key={value}
                    className={`flex items-start gap-3 rounded-lg border p-3 transition-colors ${
                      on ? 'border-[#ee785b] bg-[#ee785b]/[0.06]' : 'border-[#E2E2DF] bg-white hover:border-[#cbd5e1]'
                    }`}
                  >
                    <Checkbox
                      id={id}
                      checked={on}
                      onCheckedChange={() => toggle(q.field, value)}
                      className="mt-0.5"
                    />
                    <Label htmlFor={id} className="cursor-pointer text-sm font-normal leading-relaxed text-[#1A1A1A]">
                      {label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </fieldset>
        ) : (
          <div key={q.field}>
            <Label htmlFor={q.field} className="text-sm font-medium text-[#1A1A1A]">
              {q.label}
            </Label>
            <Select value={draft[q.field] || ''} onValueChange={(v) => set(q.field, v)}>
              <SelectTrigger id={q.field} className="mt-2" data-testid={`answer-${q.field}`}>
                <SelectValue placeholder="Not answered" />
              </SelectTrigger>
              <SelectContent>
                {q.options.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {APPLICATION_TEXT_FIELDS.map(({ field, label }) => (
          <div key={field}>
            <Label htmlFor={field} className="text-sm font-medium text-[#1A1A1A]">
              {label}
            </Label>
            <Input
              id={field}
              value={draft[field] || ''}
              onChange={(e) => set(field, e.target.value)}
              className="mt-2"
              data-testid={`answer-${field}`}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={!dirty || saving} data-testid="save-answers">
          {saving ? 'Saving…' : 'Save answers'}
        </Button>
        {dirty && (
          <span className="text-xs text-[#64748B]">
            {Object.keys(patch).length} unsaved change{Object.keys(patch).length > 1 ? 's' : ''}
          </span>
        )}
      </div>
    </div>
  );
};

export default ApplicationAnswers;
